import {
  CheckCircleIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import PropTypes from 'prop-types';
import { dateOnlyFormatter, timestampFormatter } from '../../transforms';
import RcLink from '../RcLink/RcLink';

// Regex patterns for date/timestamp detection
const ISO_TIMESTAMP_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function ActivityFeed({
  activity,
  hasNextPage = false,
  isLoadingMore = false,
  onLoadMore = null,
}) {
  const app = import.meta.env.VITE_APP_NAME;

  function getSubjectLink(id, subject) {
    if (app.includes('admin') && subject === 'customer') {
      return `/customers/users/${id}`;
    }

    if (app.includes('admin') && subject === 'referral') {
      return `/customers/referrals/${id}`;
    }

    if (app.includes('admin') && subject === 'account') {
      return `/customers/accounts/${id}`;
    }

    if (app.includes('admin') && subject === 'production order') {
      return `/production/orders/${id}`;
    }

    if (app.includes('admin') && subject === 'warranty') {
      return `/production/warranties/${id}`;
    }

    return '';
  }

  function getUserLink(id, type) {
    if (app.includes('admin') && type === 'User') {
      return `/customers/users/${id}`;
    }

    if (app.includes('admin') && type === 'AdminUser') {
      return `/admins/users/${id}`;
    }

    if (app.includes('admin') && type === 'PartnerUser') {
      return `/partners/users/${id}`;
    }

    return '';
  }

  function getIcon(eventVerb) {
    if (eventVerb === 'created' || eventVerb === 'added') {
      return (
        <CheckCircleIcon className="size-4 text-white" aria-hidden="true" />
      );
    }

    if (eventVerb === 'updated') {
      return (
        <PencilSquareIcon className="size-4 text-white" aria-hidden="true" />
      );
    }

    if (eventVerb === 'deleted' || eventVerb === 'removed') {
      return <TrashIcon className="size-4 text-white" aria-hidden="true" />;
    }

    return <div className="size-1.5 rounded-full bg-white ring-1 ring-white" />;
  }

  function formatValue(value) {
    if (value === null || value === undefined || value === '') {
      return 'empty';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    const strValue = String(value);

    // Check for ISO timestamp (e.g., "2024-01-15T10:30:00Z")
    if (ISO_TIMESTAMP_REGEX.test(strValue)) {
      return timestampFormatter(strValue) || strValue;
    }

    // Check for date-only format (e.g., "2024-01-15")
    if (DATE_ONLY_REGEX.test(strValue)) {
      return dateOnlyFormatter(strValue) || strValue;
    }

    // Convert snake_case enum values to human-readable format
    // e.g., "a_few_times_per_week" → "A Few Times Per Week"
    if (strValue.includes('_')) {
      return strValue
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return strValue;
  }

  function renderChanges(changes) {
    if (!changes) {
      return null;
    }

    // Partner view: array of field names
    if (Array.isArray(changes)) {
      return (
        <span className="ml-1 text-black/60 text-xs">
          (Changed: {changes.join(', ')})
        </span>
      );
    }

    // Admin view: object with old/new values
    if (typeof changes === 'object') {
      const entries = Object.entries(changes);
      if (entries.length === 0) {
        return null;
      }

      return (
        <ul className="mt-1 list-inside list-disc space-y-0.5 text-black/60 text-xs">
          {entries.map(([field, values]) => {
            const [oldVal, newVal] = Array.isArray(values)
              ? values
              : [null, values];
            return (
              <li key={field}>
                <span className="font-medium">{field}:</span>{' '}
                <span className="text-red-400/80 line-through">
                  {formatValue(oldVal)}
                </span>{' '}
                &rarr;{' '}
                <span className="text-green-600">{formatValue(newVal)}</span>
              </li>
            );
          })}
        </ul>
      );
    }

    return null;
  }

  return (
    <>
      <ul className="mt-6 space-y-6">
        {[...activity]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((activityItem, activityItemIdx) => (
            <li key={activityItem.id} className="relative flex gap-x-4">
              <div
                className={`${activityItemIdx === activity.length - 1 && !hasNextPage ? 'h-6' : '-bottom-6'} absolute top-0 left-0 flex w-8 justify-center`}
              >
                <div className="w-px bg-black/60" />
              </div>

              <div className="relative flex size-8 flex-none items-center justify-center rounded-full bg-rcprimary-400">
                {getIcon(activityItem.eventVerb)}
              </div>

              <div className="flex-auto">
                <div className="flex justify-between gap-x-4">
                  <p className="py-0.5 text-black/80 text-xs leading-5">
                    <RcLink
                      to={getUserLink(
                        activityItem.user.id,
                        activityItem.user.userKlass,
                      )}
                      className="font-bold text-xs"
                    >
                      {activityItem?.user?.name}
                    </RcLink>
                    &nbsp;
                    {app.includes('admin') &&
                      activityItem?.user?.memberOf?.name && (
                        <span>({activityItem?.user?.memberOf?.name})</span>
                      )}
                    &nbsp;
                    <span className="italic">{activityItem.eventVerb}</span>
                    &nbsp;
                    <span className="font-bold text-black">
                      {activityItem.eventSubject}
                    </span>
                    &nbsp;
                    <RcLink
                      to={getSubjectLink(
                        activityItem.affectedObject.id,
                        activityItem.eventSubject,
                      )}
                      textSize="text-xs"
                    >
                      {activityItem.affectedObject.name}
                    </RcLink>
                    .
                    {activityItem.eventVerb === 'updated' &&
                      Array.isArray(activityItem.changes) &&
                      renderChanges(activityItem.changes)}
                  </p>
                  <div className="flex flex-none items-center gap-x-2 py-0.5">
                    {activityItem.groupedCount > 1 && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500">
                        {activityItem.groupedCount} changes
                      </span>
                    )}
                    <time
                      dateTime={activityItem.createdAt}
                      className="text-black/60 text-xs leading-5"
                    >
                      {timestampFormatter(activityItem.createdAt)}
                    </time>
                  </div>
                </div>
                {activityItem.eventVerb === 'updated' &&
                  activityItem.changes &&
                  !Array.isArray(activityItem.changes) &&
                  renderChanges(activityItem.changes)}
              </div>
            </li>
          ))}
      </ul>

      {hasNextPage && onLoadMore && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="inline-flex items-center rounded-md bg-rcprimary-400 px-4 py-2 font-medium text-sm text-white shadow-sm hover:bg-rcprimary-500 focus:outline-none focus:ring-2 focus:ring-rcprimary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoadingMore ? (
              <>
                <svg
                  className="mr-2 -ml-1 h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Loading...
              </>
            ) : (
              'Load More Activity'
            )}
          </button>
        </div>
      )}
    </>
  );
}

ActivityFeed.propTypes = {
  activity: PropTypes.array.isRequired,
  hasNextPage: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
};

ActivityFeed.defaultProps = {
  hasNextPage: false,
  isLoadingMore: false,
  onLoadMore: null,
};

export default ActivityFeed;
